/**
 * MSML HEALTH View Models
 *
 * Transform raw API payloads into UI-friendly objects.
 * Pages consume view models — never raw API shapes.
 */

import { DIMENSIONS, DIMENSION_ORDER, VERDICTS, FLAGS } from './content';
import {
  getScoreBandColor, getScoreBandLabel, getScoreBandClass,
  getDimensionColor, getVerdictColor,
} from './tokens';


// ── Score Analysis View Model ────────────────────────────

export function toAnalysisVM(raw) {
  if (!raw) return null;

  const dimScores = raw.dimension_scores || {};
  const dimensions = DIMENSION_ORDER.map(key => ({
    key,
    label:       DIMENSIONS[key]?.label || key,
    short:       DIMENSIONS[key]?.short || key,
    description: DIMENSIONS[key]?.description || '',
    score:       dimScores[key] ?? 0,
    color:       getDimensionColor(key),
    drivers:     (raw.drivers || []).filter(d => d.dimension === key),
  }));

  const drivers = (raw.drivers || []).map(d => ({
    dimension:      d.dimension,
    dimensionLabel: DIMENSIONS[d.dimension]?.label || d.dimension,
    reason:         d.reason,
    impact:         d.impact,
    color:          getDimensionColor(d.dimension),
    positive:       d.impact > 0,
  }));

  const evidence = (raw.evidence || []).map(e => ({
    quote:          e.quote || e.quote_text || '',
    dimension:      e.dimension || 'unknown',
    dimensionLabel: DIMENSIONS[e.dimension]?.label || e.dimension,
    type:           e.type || 'neutral',
    start:          e.start,
    end:            e.end,
    color:          getDimensionColor(e.dimension),
  }));

  return {
    id:            raw.analysis_id || raw.run_id,
    runId:         raw.run_id || raw.analysis_id,
    originalText:  raw.original_text,
    hqScore:       raw.hq_score ?? 0,
    hqScoreRaw:    raw.hq_score_raw ?? 0,
    scoreBand:     getScoreBandLabel(raw.hq_score ?? 0),
    scoreBandColor:getScoreBandColor(raw.hq_score ?? 0),
    scoreBandClass:getScoreBandClass(raw.hq_score ?? 0),
    dimensions,
    drivers,
    evidence,
    flags:         raw.flags || {},
    capsApplied:   raw.caps_applied || [],
    signals:       raw.signals,
    storageMode:   raw.storage_mode,
    rubricVersion: raw.rubric_version,
    determinism:   raw.determinism || null,
    createdAt:     raw.created_at,
  };
}


// ── Optimize View Model ──────────────────────────────────

export function toOptimizeVM(raw) {
  if (!raw) return null;
  const base = toAnalysisVM(raw);

  const afterDims = raw.after_scores?.dimension_scores || {};
  const afterDimList = DIMENSION_ORDER.map(key => ({
    key,
    label: DIMENSIONS[key]?.label || key,
    score: afterDims[key] ?? null,
    color: getDimensionColor(key),
  }));

  const deltaMap = raw.delta_dimensions || {};
  const deltas = DIMENSION_ORDER.map(key => {
    const d = deltaMap[key] || {};
    return {
      key,
      label:  d.label || DIMENSIONS[key]?.label || key,
      before: d.before ?? 0,
      after:  d.after ?? 0,
      delta:  d.delta ?? 0,
      improved: (d.delta ?? 0) > 0,
    };
  });

  const judge = raw.judge_verdict || raw.judge_flags || null;
  const verdictKey = (judge?.overall_verdict || raw.judge_flags?.overall_verdict || '').toUpperCase();

  return {
    ...base,
    optimizedText:      raw.optimized_text,
    optimizationNotes:  raw.optimization_notes,
    status:             raw.status,
    blocked:            raw.blocked,
    blockReason:        raw.block_reason,

    beforeScores: raw.before_scores,
    afterScores:  raw.after_scores,
    afterDimensions: afterDimList,
    deltaOverall: raw.delta_overall ?? 0,
    deltas,

    intentPreservation: raw.intent_preservation || {},
    safetyCheck:        raw.safety_check || {},
    judgeFlags:         raw.judge_flags || {},
    judgeVerdict:       raw.judge_verdict,

    verdictLabel: VERDICTS[verdictKey]?.label || verdictKey || 'Pending',
    verdictColor: getVerdictColor(verdictKey),

    trace: raw.trace,
  };
}


// ── Report View Model ────────────────────────────────────

export function toReportVM(raw) {
  if (!raw) return null;
  const exec = raw.executive_summary || {};
  const dims = (exec.dimensions || []).map(d => ({
    key:     d.key,
    label:   d.label || DIMENSIONS[d.key]?.label || d.key,
    score:   d.score ?? 0,
    color:   getDimensionColor(d.key),
    drivers: d.drivers || [],
  }));

  return {
    runId:     raw.run_id,
    hqScore:   exec.hq_score,
    hqScoreRaw:exec.hq_score_raw,
    scoreBand: exec.score_band,
    dimensions: dims,
    flags:     exec.flags || {},
    capsApplied: exec.caps_applied || [],
    topDrivers: exec.top_drivers || [],

    input:        raw.input || {},
    optimization: raw.optimization,
    judge:        raw.judge,
    trace:        raw.trace || {},

    createdAt: raw.created_at,
  };
}


// ── History Item View Model ──────────────────────────────

export function toHistoryItemVM(raw) {
  if (!raw) return null;

  const guarded = raw.guarded_metadata || {};
  const deltaOverall = raw.delta_overall ?? null;
  const estimatedAfter = raw.estimated_hq_after ?? null;

  return {
    id:           raw.analysis_id || raw.run_id,
    hqScore:      raw.hq_score ?? 0,
    scoreBand:    getScoreBandLabel(raw.hq_score ?? 0),
    scoreBandColor:getScoreBandColor(raw.hq_score ?? 0),
    originalText: raw.original_text || '',
    storageMode:  raw.storage_mode,
    rubricVersion:raw.rubric_version,
    createdAt:    raw.created_at,
    hasOptimization: !!raw.optimized_text,
    hasJudge:     !!raw.judge_verdict,

    // Optimization deltas
    deltaOverall:    deltaOverall,
    estimatedAfter:  estimatedAfter,
    beforeHq:        raw.before_hq ?? null,

    // Dimension-level data
    dimensionScores: raw.dimension_scores || {},
    deltaDimensions: raw.delta_dimensions || {},
    drivers:         raw.drivers || [],
    evidence:        raw.evidence || [],
    flags:           raw.flags || {},
    capsApplied:     raw.caps_applied || [],

    // Guarded optimization
    guardedMode:     guarded.optimization_mode || null,
    guardedVariant:  guarded.variant_type || null,
    guardsApplied:   guarded.guards_applied || [],
    returnedOriginal:guarded.returned_original || false,
    deltaGuard:      guarded.delta_guard_activated || false,
    noHarmActivated: guarded.no_harm_activated || false,
    intentDrift:     guarded.intent_lock_result?.drift_detected || false,
    fallbackReason:  guarded.fallback_reason || null,
    rejectedReason:  guarded.rejected_reason || null,

    // Optimized text + judge
    optimizedText:   raw.optimized_text || null,
    improvements:    raw.improvements || [],
    judgeVerdict:    raw.judge_verdict || null,
  };
}


// ── Case View Model ──────────────────────────────────────

export function toCaseVM(raw) {
  if (!raw) return null;
  return {
    caseId:             raw.case_id,
    title:              raw.title,
    description:        raw.description,
    originalText:       raw.original_text,
    category:           raw.category || raw.target_profile?.domain || 'general',
    domain:             raw.target_profile?.domain,
    audience:           raw.target_profile?.audience,
    sensitivity:        raw.target_profile?.sensitivity,
    expectedHqRange:    raw.expected_hq_range,
    expectedDimRanges:  raw.expected_dimension_ranges || {},
    expectedFailModes:  raw.expected_failure_modes || [],
    expectedCaps:       raw.expected_caps || [],
    featured:           raw.featured || false,
    notes:              raw.notes || '',
  };
}


// ── Trace Item View Model ────────────────────────────────

export function toTraceItemVM(raw) {
  if (!raw) return null;
  return {
    runId:           raw.run_id,
    userId:          raw.user_id,
    hqScore:         raw.hq_score,
    hqRaw:           raw.hq_raw,
    status:          raw.status,
    rubricVersion:   raw.rubric_version,
    promptVersion:   raw.prompt_version,
    scorerModel:     raw.scoring_model_id,
    optimizerModel:  raw.optimizer_model_id,
    judgeModel:      raw.judge_model_id,
    determinismHash: raw.determinism_hash,
    processingMode:  raw.processing_mode,
    domain:          raw.domain,
    originalText:    raw.original_text,
    hasOptimization: raw.has_optimization,
    hasJudge:        raw.has_judge,
    judgeVerdict:    raw.judge_verdict,
    capsApplied:     raw.caps_applied || [],
    flags:           raw.flags || {},
    createdAt:       raw.created_at,
  };
}


// ── Flag View Model helper ───────────────────────────────

export function toFlagList(flags) {
  if (!flags) return [];
  return Object.entries(flags)
    .filter(([, v]) => v)
    .map(([key]) => ({
      key,
      label: FLAGS[key]?.label || key,
      description: FLAGS[key]?.description || '',
    }));
}
